import { ChangeDetectionStrategy, Component, DestroyRef, DOCUMENT, inject, OnInit, Renderer2, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SafeHtml } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AnnouncementComponent } from './components/announcement/announcement.component';
import { ButtonUpComponent } from './components/button-up/button-up.component';
import { NotyComponent } from './components/noty/noty.component';
import { NotyService } from './components/noty/noty.service';
import { MANAGER_PHONES, WEBSITE_HOSTNAME, WEBSITE_ORIGIN } from './constants';
import { onWindowLoad } from './helpers/on-window-load.function';
import { OopsPageComponent } from './pages/oops-page/oops-page.component';
import { AuthSyncService } from './services/auth-sync.service';
import { CustomerService } from './services/customer.service';
import { DeviceService } from './services/device-detector/device.service';
import { JsonLdService } from './services/json-ld.service';
import { MaintenanceService } from './services/maintenance.service';
import { PageRegistryService } from './services/page-registry.service';
import { PreventScrollService } from './services/prevent-scroll.service';
import { ScrollRestorationService } from './services/scroll-restoration.service';
import { WindowResizeService } from './services/window-resize.service';
import { IconSpriteComponent } from './components/icon/icon.component';
import { APP_VERSION_UPDATED_SESSION_KEY } from './services/version-check/version-check.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    OopsPageComponent,
    AnnouncementComponent,
    RouterOutlet,
    NotyComponent,
    ButtonUpComponent,
    IconSpriteComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  private document = inject<Document>(DOCUMENT);
  private jsonLdService = inject(JsonLdService);
  protected maintenanceService = inject(MaintenanceService);
  private renderer = inject(Renderer2);
  private preventScrollService = inject(PreventScrollService);
  private scrollRestorationService = inject(ScrollRestorationService);
  private windowResizeService = inject(WindowResizeService);
  private pageRegistryService = inject(PageRegistryService);
  private deviceService = inject(DeviceService);
  private customerService = inject(CustomerService);
  private authSyncService = inject(AuthSyncService);
  private notyService = inject(NotyService);
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  localBusinessJsonLd = signal<SafeHtml | undefined>(undefined);
  webSiteJsonLd = signal<SafeHtml | undefined>(undefined);

  constructor() {
    if (this.deviceService.isPlatformServer()) { return; }

    this.scrollRestorationService.init();
    this.authSyncService.init();

    if (!this.maintenanceService.isMaintenanceInProgress()) {
      this.customerService.fetchAndSetCustomer()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  ngOnInit() {
    this.setJsonLd();

    onWindowLoad(() => {
      this.handlePreventScroll();
      this.handleWindowResize();
      this.handleInlinedCriticalCss();
      this.pageRegistryService.fetchAndRegisterAllPages();
      this.handleRefreshedPageOnAppVersionUpdate();
    });
  }

  private handlePreventScroll() {
    this.preventScrollService.isEnabled$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: isEnabled => {
          if (isEnabled) {
            this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
          } else {
            this.renderer.removeStyle(this.document.body, 'overflow');
          }
        }
      });
  }

  private handleWindowResize() {
    fromEvent(window, 'resize')
      .pipe( debounceTime(500), takeUntilDestroyed(this.destroyRef) )
      .subscribe({ next: () => this.windowResizeService.resized$.next() });
  }

  private handleInlinedCriticalCss() {
    [].forEach.call(document.querySelectorAll('link[rel="stylesheet"][media="print"]'), link => {
      link.media = '';
      link.href += ''; // onload if not already loading
    });
  }

  private handleRefreshedPageOnAppVersionUpdate(): void {
    try {
      if (sessionStorage.getItem(APP_VERSION_UPDATED_SESSION_KEY) === '1') {
        sessionStorage.removeItem(APP_VERSION_UPDATED_SESSION_KEY);
        this.notyService.info(this.translate.instant('common.appUpdatedToLatest'));
      }
    } catch {
      // ignore
    }
  }

  private setJsonLd() {
    const localJsonLd = {
      '@context': "http://schema.org",
      '@type': "LocalBusiness",
      'address': {
        '@type': "PostalAddress",
        'addressLocality': "Київ",
        'addressRegion': "",
        'postalCode': "3600",
        'streetAddress': "",
      },
      'description': `В інтернет-магазин "Клондайк" Ви знайдете широкий асортимент товарів для позолотників, художників, майстринь, іконописців та просто любителів хенд-мейду.
      ↵У нас можна купити такі товари як:
      ↵- товари для золочення (поталь, дзеркальна поталь, мордан, шелак, подушка для золочення, лампензель)
      ↵- художні фарби (акрилові фарби, масляні фарби, акварельні фарби, гуаш, темпера, пастель)
      ↵- патини, воски та пігменти (бітум, рідка поталь, акрилові патини, воскові патини, металеві та художні пігменти)
      ↵- синтетичні та натуральні кисті (біличі кисті, колонок, лисиця, щетина)
      ↵- художні аксесуари та інструменти (футляри для пензлів, мастихіни) та багато іншого
      ↵Ми раді підібрати потрібний Вам матеріал та доставити його вчасно!`,
      'email': `info@${WEBSITE_HOSTNAME}`,
      'faxNumber': MANAGER_PHONES[0],
      'image': `${WEBSITE_ORIGIN}/media/seller_image/default/logo.png`,
      'name': "Klondike",
      'priceRange': "$$$",
      'telephone': MANAGER_PHONES[0],
      'url': WEBSITE_ORIGIN
    };

    const websiteJsonLd = {
      '@context': "http://schema.org",
      '@type': "WebSite",
      'about': 'Інтернет-магазин "Клондайк" Ви знайдете широкий асортимент товарів для позолотників, художників, майстринь, іконописців та просто любителів хенд-мейду.',
      'name': "Klondike",
      'url': WEBSITE_ORIGIN
    };

    this.localBusinessJsonLd.set(this.jsonLdService.getSafeJsonLd(localJsonLd));
    this.webSiteJsonLd.set(this.jsonLdService.getSafeJsonLd(websiteJsonLd));
  }

  // protected readonly AllMbiIcons = AllMbiIcons;
}
